import React from 'react';
import {Captcha} from 'recaptz';
import {useNavigate, useLocation} from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Stack,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from 'react-bootstrap';
import {FaEye, FaEyeSlash} from 'react-icons/fa';
import {useAuth} from '../auth/auth-context';
import {createUser} from '../auth/auth';
import {getAxiosErrorMessage, getErrorCode} from '../libs/helper';
import {track, identify} from '../libs/analytics';
import {
  priceMonth,
  priceBiannual,
  priceYear,
  freeTrialActive,
  freeTrialDays,
  pricingPriceIds,
  createBillingCheckoutSession,
} from './billing';

const isAsciiUppercase = (s: string) => /^[A-Z]+$/.test(s);
const isAsciiLowercase = (s: string) => /^[a-z]+$/.test(s);
const isAsciiNumber = (s: string) => /^\d+$/.test(s);

/*
  Special characters set used for password validation.
  Represented as a plain string and checked with String.prototype.includes to avoid
  unnecessary/fragile escaping inside a regex character class.
*/
const specialCharacters = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';
const isSpecialCharacter = (s: string): boolean => {
  if (s.length !== 1) {
    return false;
  }

  return specialCharacters.includes(s);
};

// Password complexity: at least 12 chars, one lowercase, one uppercase, one number, one special char.
export const isPasswordValid = (candidate: string): boolean => {
  if (candidate === '' || candidate === null) {
    return false;
  }

  let hasUpper = false;
  let hasLower = false;
  let hasTwelve = false;
  let hasNumber = false;
  let hasSpecial = false;

  if (candidate.length >= 12 && candidate.length <= 50) {
    hasTwelve = true;
  }

  for (const ch of candidate) {
    if (isAsciiLowercase(ch)) {
      hasLower = true;
    }

    if (isAsciiUppercase(ch)) {
      hasUpper = true;
    }

    if (isAsciiNumber(ch)) {
      hasNumber = true;
    }

    if (isSpecialCharacter(ch)) {
      hasSpecial = true;
    }
  }

  return hasUpper && hasLower && hasTwelve && hasNumber && hasSpecial;
};

export const specialCharsMessage =
  'Passwords must be at least 12 characters and include one lowercase letter, one uppercase letter, one number, and one special character (!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~).  New passwords must match the confirm password.';

type Props = {
  signup?: boolean; // Optional (legacy)
  signupMode?: 'standard' | 'beta';
};

function LoginPage({signup = false, signupMode}: Props): React.JSX.Element {
  const {isAuthenticated, login, loginLoading} = useAuth();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [password2, setPassword2] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPassword2, setShowPassword2] = React.useState(false);
  const [isSignup, setIsSignup] = React.useState(
    signup || signupMode !== undefined,
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [appOutdated, setAppOutdated] = React.useState(false);
  const [captchaSolved, setCaptchaSolved] = React.useState<boolean>(false);
  const [termsAgreed, setTermsAgreed] = React.useState<boolean>(false);
  const [betaKey, setBetaKey] = React.useState('');
  const [selectedPlan, setSelectedPlan] = React.useState<
    'monthly' | 'biannual' | 'yearly'
  >('monthly');
  const navigate = useNavigate();
  const location = useLocation();
  const isSubmitting = loading || loginLoading;

  // Keep isSignup in sync with route props so navigating between /login, /signup and /beta_signup
  // updates the UI correctly even when the component instance is reused by React Router.
  React.useEffect(() => {
    const nextIsSignup = Boolean(signup) || signupMode !== undefined;
    setIsSignup(nextIsSignup);
    // Clear any stale error when switching modes.
    setError(undefined);
    setAppOutdated(false);
    // Optionally reset other form state when switching modes to avoid leaking values between modes.
    setCaptchaSolved(false);
    setBetaKey('');
    setSelectedPlan('monthly');
    setPassword('');
    setPassword2('');
    if (nextIsSignup) {
      track('signup_started', {mode: signupMode ?? 'standard'});
    }
  }, [signup, signupMode]);

  const parameters = new URLSearchParams(location.search);
  const rawNext = parameters.get('next');
  const next = rawNext ? decodeURIComponent(rawNext) : '/';

  React.useEffect(() => {
    if (isAuthenticated) {
      void navigate(next, {replace: true});
    }
  }, [isAuthenticated, navigate, next]);

  const isFormValid = (): boolean => {
    if (isSubmitting || email.trim().length === 0 || password.length === 0) {
      return false;
    }

    if (isSignup && password2 !== password) {
      return false;
    }

    if (isSignup && !termsAgreed) {
      return false;
    }

    // Only require a beta key when the signup mode is explicitly 'beta'.
    if (isSignup && signupMode === 'beta' && betaKey.trim().length === 0) {
      return false;
    }

    return true;
  };

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    setLoading(true);
    setError(undefined);
    setAppOutdated(false);

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    // Simple, widely-used email regex for client-side validation.
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validate email before attempting signup
    if (isSignup && !emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      setAppOutdated(false);
      setLoading(false);
      return;
    }

    // Validate password complexity on signup
    if (isSignup && !isPasswordValid(trimmedPassword)) {
      setError(
        'Password must be at least 12 characters and include one lowercase letter, one uppercase letter, one number, and one special character',
      );
      setAppOutdated(false);
      setLoading(false);
      return;
    }

    // Require beta key only for beta-signup route
    if (isSignup && signupMode === 'beta' && betaKey.trim().length === 0) {
      setError('A beta key is required to create an account.');
      setAppOutdated(false);
      setLoading(false);
      return;
    }

    try {
      if (isSignup) {
        await (typeof betaKey === 'string' && betaKey.trim() !== ''
          ? createUser(trimmedEmail, trimmedPassword, termsAgreed, betaKey)
          : createUser(trimmedEmail, trimmedPassword, termsAgreed));
        track('signup_succeeded', {mode: signupMode ?? 'standard'});
      }

      await login(trimmedEmail, trimmedPassword);
      identify(trimmedEmail);
      track('login_succeeded');

      // If this was a signup (standard flow), start the checkout flow now that the user is authenticated.
      if (isSignup && signupMode !== 'beta') {
        try {
          const selectedPriceId = pricingPriceIds[selectedPlan] ?? undefined;
          await createBillingCheckoutSession({
            priceId: selectedPriceId,
            planKey: selectedPlan,
            successUrl: `${globalThis.location.origin}/checkout/success`,
            cancelUrl: `${globalThis.location.origin}/checkout/cancelled`,
          });
          // If checkout succeeded the browser will navigate away; otherwise continue.
          return;
        } catch (error) {
          // If checkout initiation fails, log and continue with normal SPA navigation.

          console.error('Failed to initiate checkout after signup', error);
        }
      }

      await navigate(next, {replace: true});
    } catch (error: any) {
      const errorCode = getErrorCode(error);
      if (errorCode === 'APP_OUTDATED') {
        setAppOutdated(true);
        setError(
          'A new version of the app is available. Please refresh this page and try again.',
        );
      } else {
        setAppOutdated(false);
        const message =
          getAxiosErrorMessage(error) ??
          (isSignup
            ? 'Create account failed. Please try again.'
            : 'Login failed.  Please try again.');
        setError(String(message));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container className='my-5'>
      <div className='d-flex justify-content-center'>
        <Card style={{maxWidth: 480, width: '100%'}}>
          <Card.Body>
            <Card.Title>{isSignup ? 'Create account' : 'Sign in'}</Card.Title>
            <div className='mb-2'>
              <Button
                variant='outline-primary'
                onClick={() => {
                  setIsSignup(s => !s);
                  setError(undefined);
                  setAppOutdated(false);
                }}
                disabled={isSubmitting}
              >
                {isSignup
                  ? 'Have an account? Sign in'
                  : "Don't have an account? Create one"}
              </Button>
            </div>
            {!captchaSolved && isSignup && (
              <Captcha
                type='mixed'
                length={8}
                onValidate={setCaptchaSolved}
                showSuccessAnimation
              />
            )}
            {(captchaSolved || !isSignup) && (
              <>
                {error && (
                  <Alert variant='danger'>
                    <div className='d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2'>
                      <span>{error}</span>
                      {appOutdated && (
                        <Button
                          size='sm'
                          variant='primary'
                          onClick={() => {
                            try {
                              globalThis.location.reload();
                            } catch {
                              globalThis.location.assign(
                                globalThis.location.href,
                              );
                            }
                          }}
                        >
                          Refresh
                        </Button>
                      )}
                    </div>
                  </Alert>
                )}
                <Form
                  onSubmit={event => {
                    void handleSubmit(event);
                  }}
                >
                  <Form.Group controlId='email'>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type='email'
                      value={email}
                      placeholder='e.g. daisy@example.com'
                      autoComplete='email'
                      onChange={event => {
                        setEmail(event.target.value);
                      }}
                      disabled={isSubmitting}
                      required
                    />
                  </Form.Group>

                  {isSignup && (
                    <>
                      {signupMode === 'beta' ? (
                        <Form.Group controlId='betaKey' className='mt-3'>
                          <Form.Label>Beta Key</Form.Label>
                          <Form.Control
                            type='text'
                            value={betaKey}
                            placeholder='Enter beta key'
                            autoComplete='off'
                            onChange={event => {
                              setBetaKey(event.target.value);
                            }}
                            disabled={isSubmitting}
                            required
                          />
                        </Form.Group>
                      ) : (
                        <Form.Group
                          controlId='subscriptionPlan'
                          className='mt-3'
                        >
                          <Form.Label>
                            Subscription plan (comparison{' '}
                            <a href='/pricing'>here</a>)
                          </Form.Label>
                          <div>
                            <Form.Check
                              type='radio'
                              id='plan-monthly'
                              name='subscriptionPlan'
                              label='Monthly'
                              value='monthly'
                              checked={selectedPlan === 'monthly'}
                              onChange={() => {
                                setSelectedPlan('monthly');
                              }}
                              disabled={isSubmitting}
                            />
                            <Form.Check
                              type='radio'
                              id='plan-biannually'
                              name='subscriptionPlan'
                              label='Bi-Annual'
                              value='biannual'
                              checked={selectedPlan === 'biannual'}
                              onChange={() => {
                                setSelectedPlan('biannual');
                              }}
                              disabled={isSubmitting}
                            />
                            <Form.Check
                              type='radio'
                              id='plan-yearly'
                              name='subscriptionPlan'
                              label='Yearly'
                              value='yearly'
                              checked={selectedPlan === 'yearly'}
                              onChange={() => {
                                setSelectedPlan('yearly');
                              }}
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className='mt-2'>
                            <small>
                              {`Monthly: $${priceMonth.toFixed(2)} / month · Bi-Annual: $${priceBiannual.toFixed(2)} (every 6 months) · Yearly: $${priceYear.toFixed(2)} / year`}
                              {freeTrialActive
                                ? ` · Includes ${freeTrialDays}-day free trial`
                                : ''}
                            </small>
                          </div>
                        </Form.Group>
                      )}
                    </>
                  )}

                  <Form.Group controlId='password' className='mt-3'>
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        placeholder='Your password'
                        autoComplete={
                          isSignup ? 'new-password' : 'current-password'
                        }
                        onChange={event => {
                          setPassword(event.target.value);
                        }}
                        disabled={isSubmitting}
                        required
                      />
                      <OverlayTrigger
                        placement='right'
                        delay={{show: 250, hide: 400}}
                        overlay={
                          <Tooltip>{showPassword ? 'Hide' : 'Show'}</Tooltip>
                        }
                      >
                        <Button
                          onClick={() => {
                            setShowPassword(!showPassword);
                          }}
                          variant='outline-secondary'
                          disabled={isSubmitting}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </OverlayTrigger>
                    </InputGroup>
                  </Form.Group>
                  {isSignup && (
                    <Form.Group controlId='password2' className='mt-3'>
                      <Form.Label>Confirm Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showPassword2 ? 'text' : 'password'}
                          value={password2}
                          placeholder='Your password'
                          autoComplete='new-password'
                          onChange={event => {
                            setPassword2(event.target.value);
                          }}
                          disabled={isSubmitting}
                          required
                        />
                        <OverlayTrigger
                          placement='right'
                          delay={{show: 250, hide: 400}}
                          overlay={
                            <Tooltip>{showPassword2 ? 'Hide' : 'Show'}</Tooltip>
                          }
                        >
                          <Button
                            onClick={() => {
                              setShowPassword2(!showPassword2);
                            }}
                            variant='outline-secondary'
                            disabled={isSubmitting}
                          >
                            {showPassword2 ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </OverlayTrigger>
                      </InputGroup>
                    </Form.Group>
                  )}
                  {isSignup && (
                    <>
                      <br />
                      <Alert variant='info'>{specialCharsMessage}</Alert>

                      <Form.Group controlId='terms-agree' className='mt-2'>
                        <Form.Check
                          type='checkbox'
                          label={
                            <>
                              By clicking submit you agree to our{' '}
                              <a href='/privacy_policy'>Privacy Policy</a> and{' '}
                              <a href='/terms_and_conditions'>
                                Terms and Conditions
                              </a>
                              .
                            </>
                          }
                          checked={termsAgreed}
                          onChange={event => {
                            setTermsAgreed(event.target.checked);
                          }}
                          disabled={isSubmitting}
                          required
                        />
                      </Form.Group>
                    </>
                  )}
                  <Stack direction='horizontal' gap={2} className='mt-3'>
                    <Button
                      type='submit'
                      className={isSignup ? 'btn-signup' : undefined}
                      disabled={!isFormValid() || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            animation='border'
                            size='sm'
                            className='me-2'
                          />
                          {isSignup ? 'Creating...' : 'Signing in...'}
                        </>
                      ) : isSignup ? (
                        'Create account'
                      ) : (
                        'Sign in'
                      )}
                    </Button>
                    <Button
                      variant='outline-primary'
                      type='button'
                      disabled={isSubmitting}
                      onClick={() => {
                        // Clear all form state so the button resets the component both in login and signup modes
                        setEmail('');
                        setPassword('');
                        setPassword2('');
                        setShowPassword(false);
                        setShowPassword2(false);
                        setError(undefined);
                        setAppOutdated(false);
                        setTermsAgreed(false);
                        setBetaKey('');
                        setSelectedPlan('monthly');
                      }}
                    >
                      Clear
                    </Button>
                    {!isSignup && (
                      <Button
                        variant='outline-primary'
                        type='button'
                        onClick={() => {
                          void navigate('/request_password_reset');
                        }}
                        disabled={isSubmitting}
                      >
                        Forgot password?
                      </Button>
                    )}
                  </Stack>
                </Form>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}

export function LogoutPage(): React.JSX.Element {
  const {logout} = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    (async () => {
      try {
        await logout();
      } finally {
        // Ensure we navigate to the login page after logout
        void navigate('/login', {replace: true});
      }
    })();
  }, [logout, navigate]);

  return (
    <Container className='my-5'>
      <div>Logging out...</div>
    </Container>
  );
}

export function SignupSuccessPage(): React.JSX.Element {
  const {logout} = useAuth();

  React.useEffect(() => {
    (async () => {
      try {
        await logout();
      } catch {}
    })();
  }, [logout]);

  return (
    <Container className='my-5'>
      <h1>Signup Success</h1>
      <Alert variant='info'>
        Sign up was successful! Please <a href='/login'>log in</a> again.
      </Alert>
    </Container>
  );
}

export default LoginPage;
